import { isNil, get, map, isEmpty } from 'lodash';

const ATLAS_TYPES = {
  TEMPLATESCORECARDONECOMPONENT: 'templateScorecardOneComponent',
  TEMPLATESCORECARDTWOCOMPONENTS: 'templateScorecardTwoComponents',
  TEMPLATESCORECARDTHRECOMPONENTS: 'templateScorecardThreComponents',
  TEMPLATESCORECARDFOURCOMPONENTS: 'templateScorecardFourComponents',
  TEMPLATESCORECARDSECTIONS: 'templateScorecardSections',
  TEMPLATESCORECARDIFRAMEBUTTONS: 'templateScorecardIframeButtons',
  TEMPLATESCORECARDCAROUSEL: 'templateScorecardCarousel',
  TEMPLATESCORECARDAIATLAS: 'templateScorecardAIAtlas',
  TEMPLATESCORECARDCREATEDATASET: 'templateScorecardCreateDataset',
  TEMPLATESCORECARDFOCUSEDTRAINATLAS: 'templateScorecardFocusedTrainAtlas'
};

/**
 * Gets the atlas id.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {string} The id for the atlas or empty string if not found.
 * @throws {Error} If atlas is null or undefined.
 */
export const getId = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, '_id', '');
};

/**
 * Get atlas name.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {string} The name of the atlas or empty string if not found.
 * @throws {Error} If atlas is null or undefined.
 */
export const getName = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'name', '');
};

/**
 * Get atlas owner.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {string} The owner of the atlas or empty string if not found.
 * @throws {Error} If atlas is null or undefined.
 */
export const getOwner = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'owner', '');
};

/**
 * Gets the type of the atlas.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {string} The type or empty string if not found.
 * @throws {Error} If atlas is null or undefined.
 */
export const getType = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'atlasType', '');
};

/**
 * Get atlas description.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {string} The description of the atlas or an empty string if not found.
 * @throws {Error} If atlas is null or undefined.
 */
export const getDescription = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'description', '');
};

/**
 * Get atlas image URL.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {string} The atlas image URL or empty string if not found.
 * @throws {Error} If atlas is null or undefined.
 */
export const getImageURL = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'imageUrl', '');
};

/**
 * Get atlas structure.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {Array} The atlas structure or empty array if not found.
 * @throws {Error} If atlas is null or undefined.
 */
export const getAtlasStructure = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return map(get(atlasObject, 'atlasStructure', []), 'atlasElement');
};

/**
 * Get atlas props.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {Array} The atlas props or empty array if not found.
 * @throws {Error} If atlas is null or undefined.
 */
export const getAtlasProps = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return map(get(atlasObject, 'atlasStructure', []), 'atlasProps');
};

/**
 * Check if atlas is shared.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {boolean} True if the atlas is shared and false otherwise.
 * @throws {Error} If atlas is null or undefined.
 */
export const isShared = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'shared', false);
};
/**
 * Check if atlas is shared.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {boolean} True if the atlas is shared and false otherwise.
 * @throws {Error} If atlas is null or undefined.
 */
export const isSharedPrivate = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'sharedPrivate', false);
};
/**
 * Check if map is shared.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {boolean} True if the atlas is shared and false otherwise.
 * @throws {Error} If atlas is null or undefined.
 */
export const getIsNameShared = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'sharedConfiguration.isNameShared', false);
};
/**
 * Check if atlas is shared.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {boolean} True if the atlas is shared and false otherwise.
 * @throws {Error} If atlas is null or undefined.
 */
export const isSharedUser = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'sharedUser', false);
};
/**
 * Get the public id for the atlas.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {string} The public id for the atlas or an empty string if not found.
 * @throws {Error} If atlas is null or undefined.
 */
export const getPublicId = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'publicId', '');
};

/**
 * Checks if the atlas is deleted.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {boolean} True if the atlas is in trash, false otherwise.
 * @throws {Error} If atlas is null or undefined.
 */
export const isDeleted = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'deleted', '') !== 'no';
};

/**
 * Checks if the atlas is AI type.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {boolean} True if the atlas is AI type, false otherwise.
 * @throws {Error} If atlas is null or undefined.
 */
export const isAI = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'atlasType', '') === ATLAS_TYPES.TEMPLATESCORECARDAIATLAS;
};

/**
 * Checks if the atlas is AI type.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {boolean} True if the atlas is AI type, false otherwise.
 * @throws {Error} If atlas is null or undefined.
 */
export const isDataSet = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'atlasType', '') === ATLAS_TYPES.TEMPLATESCORECARDCREATEDATASET;
};

/**
 * Checks if the atlas is focused training type.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {boolean} True if the atlas is focused training type, false otherwise.
 * @throws {Error} If atlas is null or undefined.
 */
export const isFocusedTrain = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'atlasType', '') === ATLAS_TYPES.TEMPLATESCORECARDFOCUSEDTRAINATLAS;
};

/**
 * Gets the GMS connection.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {string} The id of the GMS connection or an empty string if not found.
 * @throws {Error} If atlas is null or undefined.
 */
export const getGMSConnection = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'gmsConnection', '');
};
/**
 * Gets the GMS connection.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {string} The id of the GMS connection or an empty string if not found.
 * @throws {Error} If atlas is null or undefined.
 */
export const getGMSConnection2 = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'gmsConnection2', '');
};

/**
 * Gets the GMS connection.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {string} The id of the GMS connection or an empty string if not found.
 * @throws {Error} If atlas is null or undefined.
 */
export const getGMSDirection = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');

  if (!isEmpty(atlasObject.domain)) {
    return atlasObject.domain;
  }
  return `${atlasObject.ip}:${atlasObject.port}`;
};
/**
 * Checks if the atlas buttons are to be shown.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {boolean} False if buttons are not to be shown, true otherwise.
 * @throws {Error} If atlas is null or undefined.
 */
export const areButtonsShown = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'showButtons', true);
};

/**
 * Checks if the atlas buttons are to be shown.
 *
 * @param {object} atlasObject - The atlas object.
 * @returns {boolean} False if buttons are not to be shown, true otherwise.
 * @throws {Error} If atlas is null or undefined.
 */
export const getSharedConfiguration = atlasObject => {
  if (isNil(atlasObject)) throw new Error('atlas not defined');
  return get(atlasObject, 'sharedConfiguration', {
    userList: [],
    isNameShared: false
  });
};
